import { useState, useEffect } from 'react';
import api from '@/utils/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { Building2, Plus, Trash2, Mail, Users } from 'lucide-react';

export default function AdminFacilitiesTab() {
  const [facilities, setFacilities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAdd, setShowAdd] = useState(false);
  const [newName, setNewName] = useState('');
  const [showInvite, setShowInvite] = useState(null);
  const [inviteEmail, setInviteEmail] = useState('');
  const [confirmDelete, setConfirmDelete] = useState(null);

  const fetchFacilities = async () => {
    try {
      const res = await api.get('/facility/accounts');
      setFacilities(res.data);
    } catch { /* silent */ }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFacilities(); }, []);

  const createFacility = async () => {
    if (!newName.trim()) return;
    try {
      await api.post('/admin/facility/create', { facility_name: newName.trim() });
      toast.success('Facility created');
      setNewName('');
      setShowAdd(false);
      fetchFacilities();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const deleteFacility = async (facilityId) => {
    try {
      await api.delete(`/admin/facility/${facilityId}`);
      toast.success('Facility deleted');
      setConfirmDelete(null);
      fetchFacilities();
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  const sendInvite = async (facilityId, facilityName) => {
    if (!inviteEmail.trim()) return;
    try {
      await api.post('/admin/facility/invite', { facility_name: facilityName, email: inviteEmail.trim(), facility_id: facilityId });
      toast.success(`Invite sent to ${inviteEmail}`);
      setInviteEmail('');
      setShowInvite(null);
    } catch (err) { toast.error(err?.response?.data?.detail || 'Failed'); }
  };

  if (loading) return <div className="text-center py-8 text-slate-400">Loading facilities...</div>;

  return (
    <div className="space-y-4" data-testid="admin-facilities-tab">
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-bold text-slate-800" style={{ fontFamily: 'Nunito' }}>Aquatic Facilities</h3>
        <Button onClick={() => setShowAdd(true)} className="rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold gap-2" data-testid="add-facility-btn">
          <Plus className="w-4 h-4" /> Add Facility
        </Button>
      </div>

      {facilities.length === 0 && (
        <Card className="rounded-2xl border-dashed border-2 border-slate-200">
          <CardContent className="p-8 text-center">
            <Building2 className="w-12 h-12 text-slate-300 mx-auto mb-3" />
            <p className="text-slate-400 font-semibold">No facilities yet</p>
            <p className="text-xs text-slate-400 mt-1">Add a facility to start tracking pool admissions</p>
          </CardContent>
        </Card>
      )}

      {facilities.map(f => (
        <Card key={f.facility_id} className="rounded-2xl border-slate-100" data-testid={`facility-card-${f.facility_id}`}>
          <CardContent className="p-4">
            <div className="flex items-center gap-3 mb-3">
              <div className="w-10 h-10 rounded-xl bg-[#ECFEFF] flex items-center justify-center shrink-0">
                <Building2 className="w-5 h-5 text-[#22D3EE]" />
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-slate-800 text-sm">{f.facility_name}</p>
                <div className="flex items-center gap-2 mt-0.5 flex-wrap">
                  {f.user_id ? (
                    <Badge className="rounded-full bg-green-50 text-green-600 border-green-200 text-[10px]">Manager linked</Badge>
                  ) : (
                    <Badge className="rounded-full bg-slate-50 text-slate-500 border-slate-200 text-[10px]">No manager</Badge>
                  )}
                  {f.email && <span className="text-[10px] text-slate-400 truncate">{f.email}</span>}
                </div>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="outline" size="sm" className="rounded-full gap-1.5 text-xs flex-1 h-10" onClick={() => { setShowInvite(f); setInviteEmail(f.email || ''); }} data-testid={`invite-manager-${f.facility_id}`}>
                <Mail className="w-3.5 h-3.5" /> Invite Manager
              </Button>
              <Button variant="ghost" size="icon" className="h-10 w-10 text-red-400 hover:text-red-600 shrink-0" onClick={() => setConfirmDelete(f)} data-testid={`delete-facility-${f.facility_id}`}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          </CardContent>
        </Card>
      ))}

      {/* Add Facility Dialog */}
      <Dialog open={showAdd} onOpenChange={setShowAdd}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }}>Add Facility</DialogTitle>
            <DialogDescription className="sr-only">Create a new facility profile</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input className="rounded-xl" placeholder="e.g., Grand Forks Aquatic Centre" value={newName} onChange={e => setNewName(e.target.value)} onKeyDown={e => e.key === 'Enter' && createFacility()} data-testid="new-facility-name" />
            <Button onClick={createFacility} className="w-full rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold" data-testid="confirm-add-facility">Create Facility</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Invite Manager Dialog */}
      <Dialog open={!!showInvite} onOpenChange={() => setShowInvite(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Nunito' }}>Invite Manager — {showInvite?.facility_name}</DialogTitle>
            <DialogDescription className="sr-only">Send invite to facility manager</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <Input type="email" className="rounded-xl" placeholder="manager@example.com" value={inviteEmail} onChange={e => setInviteEmail(e.target.value)} data-testid="invite-manager-email" />
            <Button onClick={() => showInvite && sendInvite(showInvite.facility_id, showInvite.facility_name)} className="w-full rounded-full bg-[#1E1B4B] hover:bg-[#2D2B66] text-white font-bold" data-testid="send-manager-invite">Send Invitation</Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <Dialog open={!!confirmDelete} onOpenChange={() => setConfirmDelete(null)}>
        <DialogContent className="rounded-3xl max-w-sm">
          <DialogHeader>
            <DialogTitle className="text-red-600" style={{ fontFamily: 'Nunito' }}>Delete Facility</DialogTitle>
            <DialogDescription className="sr-only">Confirm facility deletion</DialogDescription>
          </DialogHeader>
          <div className="space-y-4 pt-2">
            <p className="text-sm text-slate-600">Delete <strong>{confirmDelete?.facility_name}</strong>? This will remove all admission rates for this facility. Existing admission records will be preserved.</p>
            <div className="grid grid-cols-2 gap-3">
              <Button variant="outline" className="rounded-xl font-bold" onClick={() => setConfirmDelete(null)}>Cancel</Button>
              <Button className="rounded-xl bg-red-500 hover:bg-red-600 text-white font-bold" onClick={() => confirmDelete && deleteFacility(confirmDelete.facility_id)} data-testid="confirm-delete-facility">Delete</Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
